import { useEffect, useState } from 'react';
import axios from 'axios';
import { Key, Plus, Trash2, Eye, PlayCircle, CheckCircle2, XCircle, AlertCircle, ExternalLink, BookOpen, Loader2, Code, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

export default function AICredentials() {
  const [credentials, setCredentials] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [showKeyPreview, setShowKeyPreview] = useState({});
  const [testingCredential, setTestingCredential] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [editingCredential, setEditingCredential] = useState(null);
  const [formData, setFormData] = useState({
    provider_id: '',
    environment: 'production',
    api_key: '',
    region: '',
    rate_limit_per_min: '',
    daily_quota: '',
    description: '',
    model_endpoint: '',
    integration_instructions: '',
  });
  const [showInstructions, setShowInstructions] = useState({});

  useEffect(() => {
    fetchProviders();
    fetchCredentials();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProviders(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      toast.error('Failed to fetch credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        rate_limit_per_min: formData.rate_limit_per_min ? parseInt(formData.rate_limit_per_min) : null,
        daily_quota: formData.daily_quota ? parseInt(formData.daily_quota) : null,
      };
      
      if (editingCredential) {
        // Update existing credential
        await axios.put(`${API}/credentials/${editingCredential.credential_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Credential updated successfully');
      } else {
        // Create new credential
        await axios.post(`${API}/credentials`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Credential created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      setEditingCredential(null);
      fetchCredentials();
    } catch (error) {
      console.error('Error saving credential:', error);
      toast.error(error.response?.data?.detail || 'Failed to save credential');
    }
  };

  const handleEdit = (credential) => {
    setEditingCredential(credential);
    setFormData({
      provider_id: credential.provider_id,
      environment: credential.environment,
      api_key: '', // Don't pre-fill API key for security
      region: credential.region || '',
      rate_limit_per_min: credential.rate_limit_per_min?.toString() || '',
      daily_quota: credential.daily_quota?.toString() || '',
      description: credential.description || '',
      model_endpoint: credential.model_endpoint || '',
      integration_instructions: credential.integration_instructions || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Credential deleted successfully');
      fetchCredentials();
    } catch (error) {
      console.error('Error deleting credential:', error);
      toast.error('Failed to delete credential');
    }
  };

  const testCredential = async (credentialId) => {
    setTestingCredential(credentialId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/credentials/${credentialId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setTestResults(prev => ({
        ...prev,
        [credentialId]: response.data
      }));
      
      if (response.data.success) {
        toast.success('API key is valid and working!');
      } else {
        toast.error(response.data.message || 'API key test failed');
      }
    } catch (error) {
      console.error('Error testing credential:', error);
      setTestResults(prev => ({
        ...prev,
        [credentialId]: { success: false, message: 'Test failed', tested: true }
      }));
      toast.error('Failed to test credential');
    } finally {
      setTestingCredential(null);
    }
  };

  const fetchProviderGuide = async (providerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/providers/${providerId}/guide`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedGuide(response.data);
      setShowGuideDialog(true);
    } catch (error) {
      console.error('Error fetching guide:', error);
      toast.error('Failed to fetch integration guide');
    }
  };

  const viewKeyPreview = async (credentialId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowKeyPreview(prev => ({
        ...prev,
        [credentialId]: response.data.api_key_preview
      }));
    } catch (error) {
      console.error('Error fetching key preview:', error);
      toast.error('Failed to fetch key preview');
    }
  };

  const resetForm = () => {
    setFormData({
      provider_id: '',
      environment: 'production',
      api_key: '',
      region: '',
      rate_limit_per_min: '',
      daily_quota: '',
      description: '',
      model_endpoint: '',
      integration_instructions: '',
    });
    setEditingCredential(null);
  };

  const getStatusBadge = (status) => {
    return status === 'active'
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };

  const getEnvBadge = (env) => {
    return env === 'production'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-yellow-100 text-yellow-800';
  };

  const getTestStatusIcon = (credentialId) => {
    const result = testResults[credentialId];
    if (!result) return null;
    
    if (result.success) {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    } else {
      return <XCircle className="w-4 h-4 text-red-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Loading credentials...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">API Credentials</h1>
          <p className="text-slate-600">Securely manage API keys for AI providers</p>
        </div>
        <Button
          onClick={() => {
            if (providers.length === 0) {
              toast.error('Please add a provider first');
              return;
            }
            resetForm();
            setShowDialog(true);
          }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Credential
        </Button>
      </div>

      {/* Credentials Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Provider</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Environment</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Limits</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Test</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {credentials.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                  <Key className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p>No credentials found.</p>
                  <p className="text-sm">Click "Add Credential" to create one, or view integration guides below.</p>
                </td>
              </tr>
            ) : (
              credentials.map((cred) => (
                <tr key={cred.credential_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {cred.provider_name}
                        {cred.model_endpoint && (
                          <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">
                            {cred.model_endpoint}
                          </span>
                        )}
                        <button
                          onClick={() => {
                            const provider = providers.find(p => p.provider_id === cred.provider_id);
                            if (provider) fetchProviderGuide(provider.provider_id);
                          }}
                          className="text-blue-500 hover:text-blue-700"
                          title="View Provider Guide"
                        >
                          <BookOpen className="w-4 h-4" />
                        </button>
                        {cred.integration_instructions && (
                          <button
                            onClick={() => setShowInstructions(prev => ({
                              ...prev,
                              [cred.credential_id]: !prev[cred.credential_id]
                            }))}
                            className="text-purple-500 hover:text-purple-700"
                            title="View Custom Integration Instructions"
                          >
                            <Code className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {cred.description && (
                        <div className="text-sm text-slate-500">{cred.description}</div>
                      )}
                      {showInstructions[cred.credential_id] && cred.integration_instructions && (
                        <div className="mt-2 p-3 bg-slate-50 rounded-lg border text-sm">
                          <div className="font-semibold text-slate-700 mb-1">Integration Instructions:</div>
                          <pre className="whitespace-pre-wrap text-xs text-slate-600 font-mono">
                            {cred.integration_instructions}
                          </pre>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEnvBadge(cred.environment)}`}>
                      {cred.environment}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div>{cred.rate_limit_per_min ? `${cred.rate_limit_per_min}/min` : '-'}</div>
                    <div className="text-xs text-slate-500">
                      {cred.daily_quota ? `${cred.daily_quota}/day` : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(cred.status)}`}>
                        {cred.status}
                      </span>
                      {getTestStatusIcon(cred.credential_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => testCredential(cred.credential_id)}
                      disabled={testingCredential === cred.credential_id}
                      className="flex items-center gap-1"
                    >
                      {testingCredential === cred.credential_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <PlayCircle className="w-4 h-4" />
                      )}
                      Test
                    </Button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(cred)}
                        className="text-purple-600 hover:text-purple-800"
                        title="Edit Credential"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => viewKeyPreview(cred.credential_id)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Key Preview"
                      >
                        {showKeyPreview[cred.credential_id] ? (
                          <div className="text-xs font-mono">{showKeyPreview[cred.credential_id]}</div>
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(cred.credential_id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Test Result Alert */}
      {Object.keys(testResults).length > 0 && (
        <div className="mt-4 space-y-2">
          {Object.entries(testResults).map(([credId, result]) => {
            const cred = credentials.find(c => c.credential_id === credId);
            return (
              <Alert key={credId} variant={result.success ? "default" : "destructive"} className={result.success ? "bg-green-50 border-green-200" : ""}>
                {result.success ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <AlertDescription>
                  <strong>{cred?.provider_name}:</strong> {result.message}
                </AlertDescription>
              </Alert>
            );
          })}
        </div>
      )}

      {/* Integration Guides Quick Reference */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Integration Guides
          </CardTitle>
          <CardDescription>
            Quick reference for getting API keys from popular AI providers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {providers.map((provider) => (
              <Button
                key={provider.provider_id}
                variant="outline"
                size="sm"
                onClick={() => fetchProviderGuide(provider.provider_id)}
                className="justify-start"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {provider.provider_name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingCredential ? 'Edit API Credential' : 'Add API Credential'}</DialogTitle>
            <DialogDescription>
              {editingCredential 
                ? 'Update the credential details. Leave API key empty to keep the existing key.'
                : 'Add a new API key for an AI provider. The key will be encrypted at rest.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
              <div>
                <Label htmlFor="provider_id">Provider {!editingCredential && '*'}</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.provider_id}
                    onValueChange={(value) => setFormData({...formData, provider_id: value})}
                    disabled={!!editingCredential}
                    required={!editingCredential}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((provider) => (
                        <SelectItem key={provider.provider_id} value={provider.provider_id}>
                          {provider.provider_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.provider_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fetchProviderGuide(formData.provider_id)}
                      title="View Integration Guide"
                    >
                      <BookOpen className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="environment">Environment *</Label>
                <Select
                  value={formData.environment}
                  onValueChange={(value) => setFormData({...formData, environment: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="api_key">
                  API Key {!editingCredential && '*'}
                  {editingCredential && <span className="text-xs text-slate-500 ml-2">(leave empty to keep existing)</span>}
                </Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder={editingCredential ? "Enter new API key or leave empty" : "Enter API key"}
                  value={formData.api_key}
                  onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                  required={!editingCredential}
                />
              </div>

              <div>
                <Label htmlFor="region">Region</Label>
                <Input
                  id="region"
                  placeholder="e.g., us-west-1"
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rate_limit">Rate Limit (per min)</Label>
                  <Input
                    id="rate_limit"
                    type="number"
                    placeholder="60"
                    value={formData.rate_limit_per_min}
                    onChange={(e) => setFormData({...formData, rate_limit_per_min: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="daily_quota">Daily Quota</Label>
                  <Input
                    id="daily_quota"
                    type="number"
                    placeholder="10000"
                    value={formData.daily_quota}
                    onChange={(e) => setFormData({...formData, daily_quota: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional notes"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="model_endpoint">Model Endpoint / ID</Label>
                <Input
                  id="model_endpoint"
                  placeholder="e.g., fal-ai/flux/schnell or kling-v2-5-turbo"
                  value={formData.model_endpoint}
                  onChange={(e) => setFormData({...formData, model_endpoint: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">Specific model endpoint this credential is for (useful for fal.ai)</p>
              </div>

              <div>
                <Label htmlFor="integration_instructions">Integration Instructions / Guide</Label>
                <Textarea
                  id="integration_instructions"
                  placeholder={`Enter custom integration instructions for this AI model...

Example:
- API Endpoint: https://fal.run/fal-ai/flux/schnell
- Auth Header: Authorization: Key {api_key}
- Request format: {"prompt": "...", "image_size": "landscape_4_3"}
- Response: {"images": [{"url": "..."}]}`}
                  value={formData.integration_instructions}
                  onChange={(e) => setFormData({...formData, integration_instructions: e.target.value})}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Custom instructions for how to use this credential. This will be used by the backend for integration.
                </p>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editingCredential ? 'Update Credential' : 'Create Credential'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Integration Guide Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              {selectedGuide?.name} Integration Guide
            </DialogTitle>
            <DialogDescription>
              {selectedGuide?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedGuide && (
            <div className="space-y-4 py-4">
              {/* Website & Docs Links */}
              <div className="flex gap-2">
                {selectedGuide.website && (
                  <a
                    href={selectedGuide.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Website
                  </a>
                )}
                {selectedGuide.api_docs && (
                  <a
                    href={selectedGuide.api_docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-3 h-3" />
                    API Docs
                  </a>
                )}
                {selectedGuide.pricing_url && (
                  <a
                    href={selectedGuide.pricing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Pricing
                  </a>
                )}
              </div>

              {/* How to Get API Key */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">How to Get API Key</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                  {selectedGuide.how_to_get_key?.map((step, index) => (
                    <li key={index}>{step.replace(/^\d+\.\s*/, '')}</li>
                  ))}
                </ol>
              </div>

              {/* Key Format */}
              {selectedGuide.key_format && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Key Format</h4>
                  <p className="text-sm text-slate-600 font-mono bg-slate-100 p-2 rounded">
                    {selectedGuide.key_format}
                  </p>
                </div>
              )}

              {/* Auth Header */}
              {selectedGuide.auth_header && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Authentication Header</h4>
                  <p className="text-sm text-slate-600 font-mono bg-slate-100 p-2 rounded">
                    {selectedGuide.auth_header}
                  </p>
                </div>
              )}

              {/* Rate Limits */}
              {selectedGuide.rate_limits && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Rate Limits</h4>
                  <p className="text-sm text-slate-600">{selectedGuide.rate_limits}</p>
                </div>
              )}

              {/* Supported Models */}
              {selectedGuide.supported_models?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Supported Models</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedGuide.supported_models.map((model, index) => (
                      <Badge key={index} variant="secondary">{model}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGuideDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Info Box */}
      <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex">
          <Key className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-900 mb-1">Security</h3>
            <p className="text-sm text-purple-800">
              All API keys are encrypted at rest using AES-256. Keys are never exposed in logs or responses.
              Use the "Test" button to verify credentials are working without exposing the actual key.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
